/**
 * app/screens/AddItemScreen.tsx
 * ------------------------------
 * What: Form for creating a new item with dynamic attributes and images.
 * Why: Guided entry point for building the user's inventory.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput as BaseTextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Modal,
  Image,
  SafeAreaView,
  StatusBar,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import type { RootStackParamList } from '../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { manipulateImage } from './ImageManipulation';
import * as FileSystem from 'expo-file-system';
import { getPlatform } from '../utils/platform';
import { BarCodeScanner } from 'expo-barcode-scanner';
import { usePaginatedItems } from '../../lib/DataContext';
import imageCompression from 'browser-image-compression';
import { useTheme } from '../../lib/ThemeContext';
import { CommonActions } from '@react-navigation/native';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from 'react-beautiful-dnd';
import { DndContext, DragEndEvent, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DraggableFlatList, { RenderItemParams } from 'react-native-draggable-flatlist';
import { CSSProperties } from 'react';
import ReceiptSelector, { Receipt } from '../components/ReceiptSelector';
import FormTextInput from '../components/FormTextInput';
import FormLabel from '../components/FormLabel';
import ThemedButton from '../components/ThemedButton';
import DynamicAttributes, { AttributeDefinition } from '../components/DynamicAttributes';

// Use wrapper for all RNTextInput usages.
const RNTextInput = FormTextInput as unknown as typeof BaseTextInput;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

type Room = {
  id: string;
  name: string;
};

type ItemImage = {
  id: string;
  url: string;
};

type AddItemScreenProps = {
  navigatorContext?: boolean;
};

type Picture = { id: string; uri: string };

function toPictureArray(arr: any[]): Picture[] {
  return arr.map((p) => ({ id: typeof p.id === 'string' ? p.id : p.uri, uri: p.uri }));
}

export default function AddItemScreen(props: AddItemScreenProps) {
  const navigation = useNavigation<NavigationProp>();
  const { tier } = useSubscription();
  const { refreshCounts } = useSubscription();
  const { currency, dateFormat } = useSubscription();
  const { user } = useAuth();
  const { refresh } = usePaginatedItems();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<Date | null>(null);
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState('');
  const [warrantyExpiry, setWarrantyExpiry] = useState<Date | null>(null);
  const [placeOfPurchase, setPlaceOfPurchase] = useState('');
  const [receiptFile, setReceiptFile] = useState<{ uri: string; name: string; type: string } | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showReceiptSelector, setShowReceiptSelector] = useState(false);
  const [warrantyFile, setWarrantyFile] = useState<{ uri: string; name: string; type: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);
  // Categories & Subcategories (global)
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string } | null>(null);
  const [subcategories, setSubcategories] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<{ id: string; name: string } | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  // Dynamic attributes
  const [attributeDefs, setAttributeDefs] = useState<AttributeDefinition[]>([]);
  const [dynAttributes, setDynAttributes] = useState<Record<string, any>>({});
  const hasDef = (key: string) => attributeDefs.some((d) => d.key === key);
  // Suggestion modals
  const [showSuggestCategoryModal, setShowSuggestCategoryModal] = useState(false);
  const [proposedCategoryName, setProposedCategoryName] = useState('');
  const [showSuggestSubcategoryModal, setShowSuggestSubcategoryModal] = useState(false);
  const [proposedSubcategoryName, setProposedSubcategoryName] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWarrantyPicker, setShowWarrantyPicker] = useState(false);
  const [datePickerMode, setDatePickerMode] = useState<'purchase' | 'warranty'>('purchase');
  const [tempDate, setTempDate] = useState<Date>(new Date());
  const [pictures, setPictures] = useState<{ uri: string; name: string; type: string }[]>([]);
  const [showRoomRequiredModal, setShowRoomRequiredModal] = useState(false);
  const [showNameRequiredModal, setShowNameRequiredModal] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 768;
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const { theme, themeType } = useTheme();
  const [purchaseDateError, setPurchaseDateError] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [showImageLimitError, setShowImageLimitError] = useState(false);
  const [showCategoryRequiredError, setShowCategoryRequiredError] = useState(false);
  const [showSubcategoryRequiredError, setShowSubcategoryRequiredError] = useState(false);
  // Additional documents (user-named)
  const MAX_ADDITIONAL_DOCS = 5;
  const [additionalDocs, setAdditionalDocs] = useState<Array<{ uri: string; name: string; type: string }>>([]);
  const [showAddDocNameModal, setShowAddDocNameModal] = useState(false);
  const [pendingAdditionalDocFile, setPendingAdditionalDocFile] = useState<{ uri: string; name: string; type: string } | null>(null);
  const [pendingAdditionalDocName, setPendingAdditionalDocName] = useState('');
  // Clear the image-limit error once the user removes enough images
  useEffect(() => {
    if (showImageLimitError && pictures.length <= 3) {
      setShowImageLimitError(false);
    }
  }, [showImageLimitError, pictures.length]);
  // Advisory modal before uploading receipt when important fields are missing
  const [showReceiptAdviceModal, setShowReceiptAdviceModal] = useState(false);
  const [receiptAdviceMessage, setReceiptAdviceMessage] = useState('');

  // Refs for auto-scroll to error
  const scrollViewRef = useRef<ScrollView>(null);
  const nameFieldRef = useRef<any>(null);
  const roomFieldRef = useRef<any>(null);
  const imageFieldRef = useRef<any>(null);
  const priceFieldRef = useRef<any>(null);
  const categoryFieldRef = useRef<any>(null);
  const subcategoryFieldRef = useRef<any>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Helper to scroll to a ref
  const scrollToRef = (ref: any) => {
    if (!ref?.current || !scrollViewRef.current) return;
    ref.current.measureLayout(
      scrollViewRef.current.getInnerViewNode(),
      (x: number, y: number) => {
        const offset = headerHeight + 24;
        // Offset the scroll position to account for the sticky header height
        scrollViewRef.current?.scrollTo({ y: Math.max(y - offset, 0), animated: true });
      }
    );
  };

  const currencySymbols: { [key: string]: string } = { GBP: '£', USD: '$', EUR: '€' };

  // Resolve a safe file extension from provided name or MIME type.
  const resolveFileExtension = (fileName?: string | null, mime?: string | null) => {
    const nameExt = (fileName || '').split('.').pop()?.toLowerCase();
    if (nameExt && /^[a-z0-9]{1,8}$/.test(nameExt)) return nameExt;
    const mimeToExt: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/heic': 'heic',
      'image/heif': 'heif',
    };
    if (mime && mimeToExt[mime]) return mimeToExt[mime];
    if (mime && mime.startsWith('image/')) return mime.split('/')[1];
    return 'bin';
  };

  useEffect(() => {
    fetchRooms();
    fetchCategories();
    if (Platform.OS === 'web') return;
    (async () => {
      // Request camera permission for barcode scanner on native platforms
      const { status } = await BarCodeScanner.requestPermissionsAsync();
      setHasPermission(status === 'granted');
    })();
  }, []);

  const fetchRooms = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('id, name')
        .eq('user_id', user.id) // Scope by current user to satisfy RLS and avoid overfetch
        .order('name');

      if (error) throw error;
      setRooms(data || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      Alert.alert('Error', 'Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('item_categories')
        .select('id, name')
        .order('name');
      if (error) throw error;
      setCategories(data || []);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  };

  const fetchSubcategories = async (categoryId: string) => {
    try {
      const { data, error } = await supabase
        .from('item_subcategories')
        .select('id, name')
        .eq('category_id', categoryId)
        .order('name');
      if (error) throw error;
      setSubcategories(data || []);
    } catch (e) {
      console.error('Error fetching subcategories:', e);
    }
  };

  // Fetch attribute definitions whenever subcategory changes
  useEffect(() => {
    const loadDefs = async () => {
      if (!selectedSubcategory) {
        setAttributeDefs([]);
        setDynAttributes({});
        return;
      }
      try {
        const { data, error } = await supabase
          .from('attribute_definitions')
          .select('id,key,label,data_type,required,options,ui_control,help_text,display_order')
          .eq('subcategory_id', selectedSubcategory.id)
          .order('display_order');
        if (error) throw error;
        const defs = (data || []) as AttributeDefinition[];
        setAttributeDefs(defs);
        // Seed dynamic values from existing typed fields where matching
        const seed: Record<string, any> = {};
        defs.forEach((d) => {
          if (d.key === 'make') seed[d.key] = make || null;
          if (d.key === 'model') seed[d.key] = model || null;
          if (d.key === 'serial') seed[d.key] = serialNumber || null;
        });
        setDynAttributes(seed);
      } catch (e) {
        console.error('Error loading attribute definitions:', e);
        setAttributeDefs([]);
      }
    };
    loadDefs();
  }, [selectedSubcategory, make, model, serialNumber]);

  const handlePickDocument = async (type: 'receipt' | 'warranty') => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const file = {
        uri: result.assets[0].uri,
        name: result.assets[0].name,
        type: result.assets[0].mimeType || 'application/pdf',
      };

//      if (file.size > 2 * 1024 * 1024) {
//        Alert.alert('Image too large', 'Please select an image smaller than 2MB.');
//        return;
//      }

      if (type === 'receipt') {
        setReceiptFile(file);
      } else {
        setWarrantyFile(file);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document');
    }
  };

  const handlePickAdditionalDocument = async () => {
    try {
      if (additionalDocs.length >= MAX_ADDITIONAL_DOCS) {
        Alert.alert('Limit reached', `You can upload up to ${MAX_ADDITIONAL_DOCS} additional documents.`);
        return;
      }
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const file = {
        uri: result.assets[0].uri,
        name: result.assets[0].name,
        type: result.assets[0].mimeType || 'application/octet-stream',
      };
      setPendingAdditionalDocFile(file);
      setPendingAdditionalDocName('');
      setShowAddDocNameModal(true);
    } catch (e) {
      console.error('Error picking additional document:', e);
      Alert.alert('Error', 'Failed to pick document');
    }
  };

  const confirmAddAdditionalDocument = () => {
    if (!pendingAdditionalDocFile) return;
    const trimmed = pendingAdditionalDocName.trim();
    if (!trimmed) {
      Alert.alert('Name required', 'Please enter a document name.');
      return;
    }
    setAdditionalDocs((prev) => [...prev, { uri: pendingAdditionalDocFile.uri, name: trimmed, type: pendingAdditionalDocFile.type }]);
    setPendingAdditionalDocFile(null);
    setPendingAdditionalDocName('');
    setShowAddDocNameModal(false);
  };

  const removeAdditionalDocumentAt = (index: number) => {
    setAdditionalDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePickImage = async (type: 'receipt' | 'warranty') => {
    try {
      if (Platform.OS === 'web') {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,.heic,.heif,.jpeg,.jpg,.png,.webp';
        input.style.display = 'none';

        input.onchange = async (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (!target.files?.length) {
            document.body.removeChild(input);
            return;
          }
          let file = target.files[0];
          if (file.size > 2 * 1024 * 1024) {
            try {
              file = await imageCompression(file, {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
              });
            } catch (err) {
              alert('Failed to compress image.');
              document.body.removeChild(input);
              return;
            }
          }
          if (file.size > 2 * 1024 * 1024) {
            alert('Image is still too large after compression.');
            document.body.removeChild(input);
            return;
          }
          const uri = URL.createObjectURL(file);
          const fileObj = {
            uri,
            name: file.name,
            type: file.type,
          };
          console.log('Adding file:', fileObj);
          if (type === 'receipt') {
            setReceiptFile(fileObj);
          } else {
            setWarrantyFile(fileObj);
          }
          document.body.removeChild(input);
        };

        document.body.appendChild(input);
        input.click();
        return;
      }

      // Mobile implementation
      Alert.alert(
        'Choose Image Source',
        'Select where to get the image from',
        [
          {
            text: 'Take Photo',
            onPress: async () => {
              const { status } = await ImagePicker.requestCameraPermissionsAsync();
              if (status !== 'granted') {
                Alert.alert('Permission Required', 'Please grant camera access to take photos');
                return;
              }

              const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                quality: 0.8,
              });

              if (result.canceled) return;

              try {
                const processed = await manipulateImage({
                  uri: result.assets[0].uri,
                  fileName: result.assets[0].fileName ?? undefined,
                });
                if (processed.size && processed.size > 2 * 1024 * 1024) {
                  Alert.alert('Image too large', 'Please select an image smaller than 2MB.');
                  return;
                }
                const file = {
                  uri: processed.uri,
                  name: processed.name,
                  type: processed.type,
                };
                if (type === 'receipt') {
                  setReceiptFile(file);
                } else {
                  setWarrantyFile(file);
                }
              } catch (err: any) {
                if (err.message === 'Image too large') {
                  Alert.alert('Image too large', 'Please select an image smaller than 2MB.');
                } else {
                  console.error('Error processing image:', err);
                  Alert.alert('Error', 'Failed to process image');
                }
              }
            },
          },
          {
            text: 'Choose from Library',
            onPress: async () => {
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                quality: 0.8,
              });

              if (result.canceled) return;

              try {
                const processed = await manipulateImage({
                  uri: result.assets[0].uri,
                  fileName: result.assets[0].fileName ?? undefined,
                });
                if (processed.size && processed.size > 2 * 1024 * 1024) {
                  Alert.alert('Image too large', 'Please select an image smaller than 2MB.');
                  return;
                }
                const file = {
                  uri: processed.uri,
                  name: processed.name,
                  type: processed.type,
                };
                if (type === 'receipt') {
                  setReceiptFile(file);
                } else {
                  setWarrantyFile(file);
                }
              } catch (err: any) {
                if (err.message === 'Image too large') {
                  Alert.alert('Image too large', 'Please select an image smaller than 2MB.');
                } else {
                  console.error('Error processing image:', err);
                  Alert.alert('Error', 'Failed to process image');
                }
              }
            },
          },
          {
            text: 'Cancel',
            style: 'cancel',
          },
        ],
        { cancelable: true }
      );
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handlePickItemPictures = async () => {
    try {
      if (Platform.OS === 'web') {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,.heic,.heif,.jpeg,.jpg,.png,.webp';
        input.multiple = true;
        input.style.display = 'none';

        input.onchange = async (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (!target.files?.length) {
            document.body.removeChild(input);
            return;
          }

          const files = Array.from(target.files);
          const newPictures = await Promise.all(
            files.map(async (file) => {
            if (file.size > 2 * 1024 * 1024) {
              try {
                  const compressedFile = await imageCompression(file, {
                  maxSizeMB: 2,
                  maxWidthOrHeight: 1920,
                    useWebWorker: true
                });
                  return {
                    uri: URL.createObjectURL(compressedFile),
                    name: file.name,
                    type: file.type
                  };
                } catch (error) {
                  console.error('Error compressing image:', error);
                  return null;
              }
            }
              return {
                uri: URL.createObjectURL(file),
              name: file.name,
                type: file.type
              };
            })
          );

          const validPictures = newPictures.filter((pic): pic is { uri: string; name: string; type: string } => pic !== null);
          if (validPictures.length > 0) {
            setPictures(prev => [...prev, ...validPictures]);
          }
          document.body.removeChild(input);
        };

        document.body.appendChild(input);
        input.click();
                } else {
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: true,
          aspect: [1, 1],
          quality: 0.8,
          allowsMultipleSelection: true
              });

        if (!result.canceled && result.assets.length > 0) {
          const newPictures = result.assets.map(asset => ({
                    uri: asset.uri,
            name: asset.uri.split('/').pop() || '',
            type: 'image/jpeg'
          }));
          setPictures(prev => [...prev, ...newPictures]);
        }
      }
    } catch (error) {
      console.error('Error picking images:', error);
      Alert.alert('Error', 'Failed to pick images');
    }
  };

  const reindexPictures = (arr: any[]): any[] => arr.map((img: any, idx: number) => ({ ...img, order: idx }));

  const handleDeletePicture = (index: number) => {
    const updated = pictures.filter((_: any, i: number) => i !== index);
    setPictures(reindexPictures(updated));
  };

  const handleDragEnd = (newOrder: any[]) => {
    setPictures(reindexPictures(newOrder));
  };

  const uploadFile = async (file: { uri: string; name: string; type: string }, isImage = false) => {
    try {
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}_${randomString}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const response = await fetch(file.uri);
      const blob = await response.blob();

      const { error: uploadError } = await supabase.storage
        .from('item-images')
        .upload(filePath, blob);

      if (uploadError) throw uploadError;
      return filePath;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const validatePrice = (value: string) => {
    // Allow empty value
    if (!value) {
      setPriceError('');
      return true;
    }
    
    // Check if it's a valid decimal number
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      setPriceError('Please enter a valid price (e.g., 10.99)');
      return false;
    }
    
    // Check if the number is reasonable (e.g., not too large)
    const numValue = parseFloat(value);
    if (numValue > 999999999.99) {
      setPriceError('Price seems too high');
      return false;
    }
    
    setPriceError('');
    return true;
  };

  const handlePriceChange = (value: string) => {
    // Remove any non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return;
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return;
    }
    
    setPrice(cleaned);
    validatePrice(cleaned);
  };

  const validatePurchaseDate = (date: Date) => {
    const today = new Date();
    // Compare only the date parts (ignore time)
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (selected > todayDate) {
      setPurchaseDateError('Purchase date cannot be in the future');
      return false;
    }
    setPurchaseDateError('');
    return true;
  };

  // Show advisory if place/date of purchase missing before opening receipt flow
  const handleAddReceiptPress = () => {
    const missingPlace = !placeOfPurchase.trim();
    const missingDate = !purchaseDate;
    if (missingPlace || missingDate) {
      let msg = 'It is recommended to add ';
      if (missingPlace && missingDate) msg += 'a place of purchase and a date of purchase';
      else if (missingPlace) msg += 'a place of purchase';
      else msg += 'a date of purchase';
      msg += ' before uploading your receipt';
      setReceiptAdviceMessage(msg);
      setShowReceiptAdviceModal(true);
    } else {
      setShowReceiptModal(true);
    }
  };

  // Override handleSave for modern UI to scroll to error
  const handleModernSave = async () => {
    if (!name.trim()) {
      setShowNameRequiredModal(true);
      scrollToRef(nameFieldRef);
      return;
    }
    if (!selectedRoom) {
      setShowRoomRequiredModal(true);
      scrollToRef(roomFieldRef);
      return;
    }
    if (!selectedCategory || !selectedSubcategory) {
      setShowCategoryRequiredError(!selectedCategory);
      setShowSubcategoryRequiredError(!selectedSubcategory);
      if (!selectedCategory) {
        scrollToRef(categoryFieldRef);
      } else if (!selectedSubcategory) {
        scrollToRef(subcategoryFieldRef);
      }
      return;
    }
    if (pictures.length > 3) {
      setShowImageLimitError(true);
      scrollToRef(imageFieldRef); // Surface the image cap constraint near the grid
      return;
    }
    if (price && !validatePrice(price)) {
      scrollToRef(priceFieldRef);
      return;
    }
    await handleSave();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setShowNameRequiredModal(true);
      return;
    }
    if (price && !validatePrice(price)) {
      scrollToRef(priceFieldRef);
      return;
    }

    try {
      setLoading(true);
      if (!user) {
        return;
      }
      if (!selectedRoom) {
        setShowRoomRequiredModal(true);
        return;
      }

      // Upload files if they exist (store first, then create DB rows referencing the paths)
      let receiptFilePath: string | null = null;
      let receiptId: string | null = selectedReceipt ? selectedReceipt.id : null;
      let warrantyFilePath = null;

      if (receiptFile) {
        const fileExt = receiptFile.name.split('.').pop();
        if (!fileExt) {
          throw new Error('Invalid file format');
        }
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;
        const response = await fetch(receiptFile.uri);
        const blob = await response.blob();
        const { error: uploadError } = await supabase.storage
          .from('item-documents')
          .upload(filePath, blob, { upsert: true }); // upsert: allow re-generation without duplicates
        if (uploadError) {
          throw uploadError;
        }
        receiptFilePath = filePath;

        // insert receipt row and capture id
        const { data: receiptData, error: receiptError } = await supabase
          .from('receipts')
          .insert({
            user_id: user.id,
            file_path: filePath,
            place_of_purchase: placeOfPurchase.trim() || null,
            purchase_date: purchaseDate ? purchaseDate.toISOString() : null,
          })
          .select()
          .single();
        if (receiptError) throw receiptError;
        receiptId = receiptData.id;
      }

      if (warrantyFile) {
        const ext = resolveFileExtension(warrantyFile.name, warrantyFile.type);
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
        const filePath = `${user.id}/${fileName}`;
        const response = await fetch(warrantyFile.uri);
        const blob = await response.blob();
        const safeContentType = warrantyFile.type && !warrantyFile.type.includes('*')
          ? warrantyFile.type
          : (blob.type || (ext === 'pdf' ? 'application/pdf' : 'application/octet-stream'));
        const { error: uploadError } = await supabase.storage
          .from('item-documents')
          .upload(filePath, blob, { upsert: true, contentType: safeContentType });
        if (uploadError) {
          throw uploadError;
        }
        warrantyFilePath = filePath;
      }

      // Upload pictures
      const uploadedPicturePaths = await Promise.all(
        pictures.map(async (file) => {
          if (!user) return null;
          // Get file extension from the name property, with fallback to jpg
          const fileExt = 'jpg';
          // Create a simpler file path structure - just user_id/filename
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;
          const response = await fetch(file.uri);
          const blob = await response.blob();
          const { error: uploadError } = await supabase.storage
            .from('item-images')
            .upload(filePath, blob);
          if (uploadError) {
            console.error('Upload error:', uploadError);
            throw uploadError;
          }
          return filePath;
        })
      );

      // Insert the item
      const { data: itemData, error: itemError } = await supabase
        .from('items')
        .insert([
          {
            name: name.trim(),
            description: description.trim() || null,
            attributes: {
              ...dynAttributes,
              ...(make ? { make: make.trim() } : {}),
              ...(model ? { model: model.trim() } : {}),
              ...(serialNumber ? { serial: serialNumber.trim() } : {}),
            },
            purchase_date: purchaseDate?.toISOString() || null,
            price: price ? parseFloat(price) : null,
            warranty_expiry: warrantyExpiry?.toISOString() || null,
            place_of_purchase: placeOfPurchase.trim() || null,
            receipt_id: receiptId,
            room_id: selectedRoom?.id,
            subcategory_id: selectedSubcategory?.id,
            user_id: user.id,
            created_with: getPlatform(),
            updated_with: getPlatform(),
          }
        ])
        .select()
        .single();

      if (itemError) {
        console.error('Error inserting item:', itemError);
        throw itemError;
      }

      // Insert warranty document record (receipts handled separately)
      if (warrantyFilePath) {
        await supabase.from('item_documents').insert({
          item_id: itemData.id,
          user_id: user.id,
          type: 'warranty',
          file_path: warrantyFilePath,
        });
      }

      // Insert additional documents
      if (additionalDocs.length > 0) {
        for (const doc of additionalDocs) {
          const ext = resolveFileExtension(doc.name, doc.type);
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
          const storagePath = `${user.id}/${itemData.id}/${fileName}`;
          const resp = await fetch(doc.uri);
          const blob = await resp.blob();
          const safeContentType = doc.type && !doc.type.includes('*') ? doc.type : (blob.type || 'application/octet-stream');
          console.log('Uploading additional doc to path:', storagePath, 'contentType:', safeContentType);
          let uploadErr = null as any;
          try {
            const { error } = await supabase.storage
              .from('item-documents')
              .upload(storagePath, blob, { upsert: true, contentType: safeContentType });
            uploadErr = error;
          } catch (e) {
            uploadErr = e;
          }
          if (uploadErr) {
            console.error('Additional doc upload failed:', uploadErr);
            throw uploadErr;
          }
          const { error: insErr } = await supabase.from('item_documents').insert({
            item_id: itemData.id,
            user_id: user.id,
            type: 'other',
            name: doc.name,
            file_path: storagePath,
            mime_type: safeContentType,
          });
          if (insErr) throw insErr;
        }
      }

      // Insert images
      if (uploadedPicturePaths.length > 0) {
        const { error: imageError } = await supabase
          .from('item_images')
          .insert(
            uploadedPicturePaths.map((filePath, idx) => ({
              item_id: itemData.id,
              url: filePath,
              order: idx
            }))
          );

        if (imageError) {
          console.error('Error inserting images:', imageError);
          throw imageError;
        }
      }

      // No need to update order separately since we set it during insert
      await refreshCounts();
      await refresh();
      navigation.dispatch(
        CommonActions.navigate({
          name: 'ItemsTab',
          params: { refresh: true }
        })
      );
    } catch (error) {
      console.error('Error adding item:', error);
      Alert.alert('Error', 'Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const handleBarCodeScanned = ({ data }: { data: string }) => {
    setShowScanner(false);
    setSerialNumber(data);
  };

  const startScanning = async () => {
    if (Platform.OS === 'web') return;
    if (hasPermission === null) {
      Alert.alert('Permission needed', 'Please grant camera permission to scan barcodes');
      return;
    }
    if (hasPermission === false) {
      Alert.alert('No access', 'Camera permission is required to scan barcodes');
      return;
    }
    setShowScanner(true);
  };

  const renderDatePicker = () => {
    if (!showDatePicker && !showWarrantyPicker) return null;

    if (Platform.OS === 'web') {
      return (
        <Modal
          key={dateFormat}
          visible={showDatePicker || showWarrantyPicker}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setShowDatePicker(false);
            setShowWarrantyPicker(false);
          }}
          accessibilityViewIsModal
          statusBarTranslucent
        >
          <View 
            style={{ backgroundColor: 'rgba(0,0,0,0.4)', flex: 1, justifyContent: 'center', alignItems: 'center' }}
          >
            <View 
              style={{ backgroundColor: theme.card, borderRadius: 20, padding: 24, maxWidth: 400, width: '90%', alignSelf: 'center', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
              onTouchEnd={e => e.stopPropagation()}
            >
              {/* Title and close button */}
              <Text style={[styles.modalTitle, { color: theme.text, textAlign: 'center', marginBottom: 24, width: '100%' }]}>
                {datePickerMode === 'purchase' ? 'Select Purchase Date' : 'Select Warranty Expiry'}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setShowDatePicker(false);
                  setShowWarrantyPicker(false);
                }}
                style={{ position: 'absolute', top: 16, right: 16, backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border, padding: 4, zIndex: 2 }}
                accessibilityRole="button"
                accessibilityLabel="Close date picker"
                accessibilityHint="Close the date picker without selecting a date"
              >
                <Ionicons name="close" size={24} color={theme.text} />
              </TouchableOpacity>

              {/* Date input */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%', marginBottom: 28 }}>
                <input
                  type="date"
                  value={tempDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newDate = new Date(value);
                    if (!isNaN(newDate.getTime())) {
                      setTempDate(newDate);
                      if (datePickerMode === 'purchase') {
                        validatePurchaseDate(newDate);
                      }
                      if (datePickerMode === 'warranty') {
                        setWarrantyExpiry(newDate);
                      }
                    }
                  }}
                  style={{
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                    width: '100%',
                    maxWidth: '300px',
                    marginRight: 8,
                  }}
                  aria-label={datePickerMode === 'purchase' ? 'Purchase date' : 'Warranty expiry date'}
                />
              </div>

              {/* Show error message in modal if purchase date is invalid */}
              {datePickerMode === 'purchase' && purchaseDateError ? (
                <Text style={{ color: theme.error || '#d32f2f', fontWeight: '600', fontSize: 14, marginBottom: 12, textAlign: 'center' }}>
                  {purchaseDateError}
                </Text>
              ) : null}

              {/* Action buttons */}
              <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 16, width: '100%' }}>
                <ThemedButton
                  title="Cancel"
                  variant="secondary"
                  onPress={() => {
                    setShowDatePicker(false);
                    setShowWarrantyPicker(false);
                  }}
                  style={{ flex: 1 }}
                />
                <ThemedButton
                  title="Confirm"
                  variant="primary"
                  onPress={() => {
                    if (datePickerMode === 'purchase') {
                      if (validatePurchaseDate(tempDate)) {
                        setPurchaseDate(tempDate);
                        setShowDatePicker(false);
                      }
                    } else {
                      setWarrantyExpiry(tempDate);
                      setShowWarrantyPicker(false);
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </View>
            </View>
          </View>
        </Modal>
      );
    }

    // For native platforms, use the native date picker
    const DateTimePicker = require('@react-native-community/datetimepicker').default;
    return (
      <DateTimePicker
        value={datePickerMode === 'purchase' ? (purchaseDate || new Date()) : (warrantyExpiry || new Date())}
        mode="date"
        display="default"
        onChange={(event: any, selectedDate?: Date) => {
          if (event.type === 'set' && selectedDate) {
            if (datePickerMode === 'purchase') {
              if (validatePurchaseDate(selectedDate)) {
                setPurchaseDate(selectedDate);
                setShowDatePicker(false);
              }
            } else {
              setWarrantyExpiry(selectedDate);
              setShowWarrantyPicker(false);
            }
          } else {
            setShowDatePicker(false);
            setShowWarrantyPicker(false);
          }
        }}
      />
    );
  };

  const renderRoomModal = () => (
    <Modal
      visible={showRoomModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowRoomModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}>
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}>
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Room</Text>
            <TouchableOpacity
              onPress={() => setShowRoomModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {rooms.map((room) => (
              <TouchableOpacity
                key={room.id}
                style={[
                  styles.roomOption,
                  selectedRoom?.id === room.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedRoom(room);
                  setShowRoomModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedRoom?.id === room.id && { color: theme.accent }, selectedRoom?.id !== room.id && { color: theme.text }]}>
                  {room.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderCategoryModal = () => (
    <Modal
      visible={showCategoryModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowCategoryModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}> 
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}> 
          <View style={styles.modalHeader}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Category</Text> 
            <TouchableOpacity
              onPress={() => setShowCategoryModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {categories.map((cat) => (
              <TouchableOpacity
                key={cat.id}
                style={[
                  styles.roomOption,
                  selectedCategory?.id === cat.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedCategory(cat);
                  setShowCategoryRequiredError(false);
                  setSelectedSubcategory(null);
                  setShowSubcategoryRequiredError(false);
                  fetchSubcategories(cat.id);
                  setShowCategoryModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedCategory?.id === cat.id && { color: theme.accent }, selectedCategory?.id !== cat.id && { color: theme.text }]}>
                  {cat.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 12 }} />
          <ThemedButton
            title="Suggest new category"
            variant="secondary"
            onPress={() => { setShowCategoryModal(false); setShowSuggestCategoryModal(true); }}
            style={{ alignSelf: 'flex-start' }}
          />
        </View>
      </View>
    </Modal>
  );

  const renderSubcategoryModal = () => (
    <Modal
      visible={showSubcategoryModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowSubcategoryModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}> 
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}> 
          <View style={styles.modalHeader}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Subcategory</Text> 
            <TouchableOpacity
              onPress={() => setShowSubcategoryModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {subcategories.map((sub) => (
              <TouchableOpacity
                key={sub.id}
                style={[
                  styles.roomOption,
                  selectedSubcategory?.id === sub.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedSubcategory(sub);
                  setShowSubcategoryRequiredError(false);
                  setShowSubcategoryModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedSubcategory?.id === sub.id && { color: theme.accent }, selectedSubcategory?.id !== sub.id && { color: theme.text }]}>
                  {sub.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 12 }} />
          <ThemedButton
            title="Suggest new subcategory"
            variant="secondary"
            onPress={() => { setShowSubcategoryModal(false); setShowSuggestSubcategoryModal(true); }}
            style={{ alignSelf: 'flex-start' }}
          />
        </View>
      </View>
    </Modal>
  );

  const submitCategorySuggestion = async () => {
    if (!user) return;
    if (!proposedCategoryName.trim()) {
      Alert.alert('Category name required');
      return;
    }
    try {
      setSuggestLoading(true);
      const { error } = await supabase
        .from('taxonomy_requests')
        .insert({
          user_id: user.id,
          kind: 'category',
          proposed_category_name: proposedCategoryName.trim(),
          status: 'new'
        });
      if (error) throw error;
      setProposedCategoryName('');
      setShowSuggestCategoryModal(false);
      Alert.alert('Suggestion submitted', 'Thank you for your suggestion.');
    } catch (e) {
      console.error('Error submitting category suggestion:', e);
      Alert.alert('Error', 'Failed to submit suggestion.');
    } finally {
      setSuggestLoading(false);
    }
  };

  const submitSubcategorySuggestion = async () => {
    if (!user || !selectedCategory) return;
    if (!proposedSubcategoryName.trim()) {
      Alert.alert('Subcategory name required');
      return;
    }
    try {
      setSuggestLoading(true);
      const { error } = await supabase
        .from('taxonomy_requests')
        .insert({
          user_id: user.id,
          kind: 'subcategory',
          proposed_subcategory_name: proposedSubcategoryName.trim(),
          parent_category_id: selectedCategory.id,
          status: 'new'
        });
      if (error) throw error;
      setProposedSubcategoryName('');
      setShowSuggestSubcategoryModal(false);
      Alert.alert('Suggestion submitted', 'Thank you for your suggestion.');
    } catch (e) {
      console.error('Error submitting subcategory suggestion:', e);
      Alert.alert('Error', 'Failed to submit suggestion.');
    } finally {
      setSuggestLoading(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return date.toLocaleDateString('en-GB');
      case 'MM/DD/YYYY':
        return date.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      default:
        return date.toLocaleDateString('en-GB');
    }
  };

  // Helper to detect mobile web
  const isMobileWeb = Platform.OS === 'web' && (typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const imageSquareSize = Platform.OS === 'web' ? (isMobileWeb ? 90 : 100) : 90;

  // Web drag-and-drop component using dnd-kit
  const WebImageGrid = React.memo(({ pictures, onReorder, onDelete, onAdd }: { pictures: Picture[]; onReorder: (pictures: Picture[]) => void; onDelete: (id: string) => void; onAdd: () => void }) => {
    const [internalOrder, setInternalOrder] = useState<Picture[]>(pictures);

    useEffect(() => {
      setInternalOrder(pictures);
    }, [pictures]);

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      if (active.id !== over.id) {
        setInternalOrder((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const newOrder = arrayMove(items, oldIndex, newIndex);
          onReorder(newOrder);
          return newOrder;
        });
      }
    };

    return (
      <DndContext
        sensors={useSensors(
          useSensor(PointerSensor, {
            activationConstraint: {
              distance: 8,
            },
          })
        )}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={internalOrder.map((item: Picture) => item.id)} strategy={verticalListSortingStrategy}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {internalOrder.map((picture: Picture) => (
              <SortableImage key={picture.id} picture={picture} onDelete={onDelete} size={imageSquareSize} />
            ))}
            {internalOrder.length < 3 && (
              <button
                onClick={onAdd}
                style={{
                  width: imageSquareSize,
                  height: imageSquareSize,
                  border: '2px dashed #007AFF',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  color: '#007AFF',
                  fontSize: 32,
                  cursor: 'pointer',
                  margin: 4,
                }}
                aria-label="Add Image"
              >
                +
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>
    );
  });

  const SortableImage = ({ picture, onDelete, size = 100 }: { picture: Picture; onDelete: (id: string) => void; size?: number }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: picture.id });
    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      width: `${size}px`,
      height: `${size}px`,
      position: 'relative',
      border: '1px solid #ccc',
      borderRadius: '4px',
      overflow: 'hidden',
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <img src={picture.uri} alt={`Image ${picture.id}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        <button
          onClick={() => onDelete(picture.id)}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: 'rgba(255, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
          }}
        >
          X
        </button>
      </div>
    );
  };

  // Native drag-and-drop component using react-native-draggable-flatlist
  const NativeImageGrid = ({ pictures, onReorder, onDelete }: { pictures: Picture[]; onReorder: (pictures: Picture[]) => void; onDelete: (id: string) => void }) => {
    const renderItem = ({ item, drag, isActive }: RenderItemParams<Picture>) => (
      <View style={{ position: 'relative', width: Platform.OS === 'web' ? 100 : 90, height: Platform.OS === 'web' ? 100 : 90, borderRadius: 8, backgroundColor: theme.inputBackground, borderWidth: 1, borderColor: theme.border, justifyContent: 'center', alignItems: 'center' }}>
        <Image source={{ uri: item.uri }} style={{ width: '100%', height: '100%', borderRadius: 6 }} resizeMode="contain" />
        <TouchableOpacity style={{ position: 'absolute', top: -8, right: -8, backgroundColor: theme.card, borderRadius: 12 }} onPress={() => onDelete(item.id)}>
          <Ionicons name="close-circle" size={24} color={theme.text} />
        </TouchableOpacity>
      </View>
    );

    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        <DraggableFlatList
          data={pictures}
          renderItem={renderItem}
          keyExtractor={(item: Picture) => item.id}
          onDragEnd={({ data }) => onReorder(data)}
          horizontal={true}
        />
        {pictures.length < 3 && (
          <TouchableOpacity
            onPress={handlePickItemPictures}
            style={{
              width: Platform.OS === 'web' ? 100 : 90,
              height: Platform.OS === 'web' ? 100 : 90,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: '#007AFF',
              borderRadius: 8,
              justifyContent: 'center',
              alignItems: 'center',
              margin: 4,
            }}
            accessibilityRole="button"
            accessibilityLabel="Add Image"
          >
            <Text style={{ color: '#007AFF', fontSize: 32 }}>+</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  if (loadingRooms) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  // --- Modern Add Item Layout ONLY ---
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}> 
      <StatusBar barStyle="light-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          stickyHeaderIndices={[0]}
        >
          {/* Modern Header */}
          <View
            onLayout={({ nativeEvent }) => setHeaderHeight(nativeEvent.layout.height)}
            style={{ paddingTop: 32, paddingBottom: 16, paddingHorizontal: 20, alignItems: 'center', backgroundColor: theme.background }}>
            <View style={{ position: 'absolute', left: 20, top: 32 }}>
              <TouchableOpacity
                style={{ padding: 8, borderRadius: 20, backgroundColor: theme.inputBackground }}
                onPress={() => navigation.goBack()}
                accessibilityLabel="Back"
                accessibilityRole="button"
              >
                <Ionicons name="arrow-back" size={26} color={theme.text} />
              </TouchableOpacity>
            </View>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 4 }}>Add Item</Text>
            <View style={{ height: 2, backgroundColor: theme.border, width: 60, borderRadius: 2, marginTop: 8 }} />
          </View>

          {/* Modern Card Form */}
          <View style={{ padding: 20, alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.card, borderRadius: 16, padding: 24, width: '100%', maxWidth: 600, ...Platform.select({
              ios: {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25, shadowRadius: 3.84,
              },
              android: {
                elevation: 3,
              },
              web: {
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
              },
            }),
            ...(themeType === 'blueNeon' ? (Platform.OS === 'web' ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 6, elevation: 6 }) : {}),
            }}>
              {/* General Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>General Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Name *</FormLabel>
                <View>
                  <RNTextInput
                    ref={nameFieldRef}
                    style={{
                      backgroundColor: theme.inputBackground,
                      color: theme.text,
                      borderColor: showNameRequiredModal ? (theme.error || '#d32f2f') : theme.border,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      fontSize: 16,
                    }}
                    value={name}
                    onChangeText={(text) => {
                      setName(text);
                      if (showNameRequiredModal) {
                        setShowNameRequiredModal(false);
                      }
                    }}
                    placeholder="Enter item name"
                    placeholderTextColor={theme.secondaryText}
                    autoCapitalize="words"
                    maxLength={25}
                  />
                  {showNameRequiredModal && (
                    <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Item name is required</Text>
                  )}
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{name.length}/25 characters</Text>
                </View>
                {/* Room moved directly below Name */}
                <FormLabel>Room *</FormLabel>
                <TouchableOpacity
                  ref={roomFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showRoomRequiredModal ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => {
                    setShowRoomModal(true);
                    if (showRoomRequiredModal) {
                      setShowRoomRequiredModal(false);
                    }
                  }}
                >
                  <Text style={{ color: selectedRoom ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedRoom ? selectedRoom.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : 'Select a room'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showRoomRequiredModal && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Room is required</Text>
                )}
                <FormLabel>Category *</FormLabel>
                <TouchableOpacity
                  ref={categoryFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showCategoryRequiredError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => setShowCategoryModal(true)}
                >
                  <Text style={{ color: selectedCategory ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedCategory ? selectedCategory.name.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase()) : 'Select a category'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showCategoryRequiredError && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Category is required</Text>
                )}

                <FormLabel>Subcategory *</FormLabel>
                <TouchableOpacity
                  ref={subcategoryFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showSubcategoryRequiredError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => {
                    if (!selectedCategory) {
                      Alert.alert('Select category first');
                      return;
                    }
                    setShowSubcategoryModal(true);
                  }}
                >
                  <Text style={{ color: selectedSubcategory ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedSubcategory ? selectedSubcategory.name.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase()) : 'Select a subcategory'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showSubcategoryRequiredError && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Subcategory is required</Text>
                )}
                {/* Dynamic attributes moved up below subcategory (exclude core fields) */}
                {attributeDefs.length > 0 ? (
                  <>
                    <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 16 }}>Details</Text>
                    <DynamicAttributes
                      definitions={attributeDefs.filter((d) => !['make', 'model', 'serial', 'serial_number'].includes(d.key))}
                      values={dynAttributes}
                      onChange={setDynAttributes}
                    />
                  </>
                ) : null}
                {hasDef('make') && (
                  <>
                    <FormLabel>Make</FormLabel>
                    <View>
                      <RNTextInput
                        style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                        value={make}
                        onChangeText={setMake}
                        placeholder="Enter manufacturer"
                        placeholderTextColor={theme.secondaryText}
                        maxLength={25}
                      />
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{make.length}/25 characters</Text>
                    </View>
                  </>
                )}
                {hasDef('model') && (
                  <>
                    <FormLabel>Model</FormLabel>
                    <View>
                      <RNTextInput
                        style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                        value={model}
                        onChangeText={setModel}
                        placeholder="Enter model number/name"
                        placeholderTextColor={theme.secondaryText}
                        maxLength={25}
                      />
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{model.length}/25 characters</Text>
                    </View>
                  </>
                )}
                {hasDef('serial') && (
                  <>
                    <FormLabel>Serial Number</FormLabel>
                    <View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <RNTextInput
                          style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16, flex: 1 }}
                          value={serialNumber}
                          onChangeText={setSerialNumber}
                          placeholder="Enter serial number"
                          placeholderTextColor={theme.secondaryText}
                          autoCapitalize="none"
                          maxLength={25}
                        />
                        {Platform.OS !== 'web' && (
                          <TouchableOpacity
                            style={{ height: 44, width: 44, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.inputBackground, borderRadius: 8, borderColor: theme.border, borderWidth: 1 }}
                            onPress={startScanning}
                          >
                            <Ionicons name="scan-outline" size={24} color={theme.accent} />
                          </TouchableOpacity>
                        )}
                      </View>
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{serialNumber.length}/25 characters</Text>
                    </View>
                  </>
                )}
                <FormLabel>Description</FormLabel>
                <View>
                  <RNTextInput
                    multiline
                    numberOfLines={9}
                    maxLength={600}
                    style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16, minHeight: 80, textAlignVertical: 'top' }}
                    value={description}
                    onChangeText={setDescription}
                    placeholder="Enter item description"
                    placeholderTextColor={theme.secondaryText}
                  />
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{description.length}/600 characters</Text>
                </View>
              
              
              
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />

              {/* Purchase Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>Purchase Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Price ({currencySymbols[currency]})</FormLabel>
                <RNTextInput
                  ref={priceFieldRef}
                  style={{
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    borderColor: priceError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    fontSize: 16
                  }}
                  value={price}
                  onChangeText={handlePriceChange}
                  placeholder="Enter price"
                  placeholderTextColor={theme.secondaryText}
                  keyboardType="numeric"
                  inputMode="decimal"
                />
                {priceError ? (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>
                    {priceError}
                  </Text>
                ) : null}
                <FormLabel>Purchase Date</FormLabel>
                <TouchableOpacity
                  style={{
                    backgroundColor: theme.inputBackground,
                    borderColor: theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                    justifyContent: 'center'
                  }}
                  onPress={() => { setDatePickerMode('purchase'); setShowDatePicker(true); }}
                >
                  <Text style={{ color: purchaseDate ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {purchaseDate ? formatDate(purchaseDate) : 'Select date'}
                  </Text>
                </TouchableOpacity>
                {/* Place of Purchase moved below */}
                <FormLabel>Place of Purchase</FormLabel>
                <View>
                  <RNTextInput
                    style={{
                      backgroundColor: theme.inputBackground,
                      color: theme.text,
                      borderColor: theme.border,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      fontSize: 16
                    }}
                    value={placeOfPurchase}
                    onChangeText={setPlaceOfPurchase}
                    placeholder="Enter place of purchase"
                    placeholderTextColor={theme.secondaryText}
                    maxLength={25}
                  />
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{placeOfPurchase.length}/25 characters</Text>
                </View>
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />

              

              {/* Warranty Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>Warranty Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Warranty Expiry</FormLabel>
                <TouchableOpacity
                  style={{ backgroundColor: theme.inputBackground, borderColor: theme.border, borderWidth: 1, borderRadius: 8, padding: 12, marginBottom: 0, height: 44, justifyContent: 'center' }}
                  onPress={() => { setDatePickerMode('warranty'); setShowWarrantyPicker(true); }}
                >
                  <Text style={{ color: warrantyExpiry ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {warrantyExpiry ? formatDate(warrantyExpiry) : 'Select date'}
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />

              {/* Documents and Images Section */}
              <View ref={imageFieldRef}>
                <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 24 }}>Documents and Images</Text>
                <View style={{ gap: 16 }}>
                  {/* Receipt */}
                  <View style={{ marginBottom: 28 }}>
                    <FormLabel style={{ marginBottom: 8 }}>Receipt</FormLabel>
                    <ThemedButton
                      title={receiptFile || selectedReceipt ? 'Change Receipt' : 'Upload Receipt'}
                      onPress={handleAddReceiptPress}
                      style={{ width: 220, alignSelf: 'flex-start' }}
                    />
                    {receiptFile || selectedReceipt ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                        <Ionicons name="document" size={18} color={theme.accent} />
                        <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6 }}>
                          {receiptFile ? 'Receipt' : (selectedReceipt?.place_of_purchase || 'Receipt')}
                        </Text>
                        <TouchableOpacity onPress={() => {
                          if (receiptFile) {
                            setReceiptFile(null);
                          }
                          if (selectedReceipt) {
                            setSelectedReceipt(null);
                          }
                        }} style={{ marginLeft: 12 }}>
                          <Ionicons name="trash" size={22} color={theme.error || '#d32f2f'} />
                        </TouchableOpacity>
                      </View>
                    ) : null}
                  </View>

                  {/* Warranty Document */}
                  <View style={{ marginBottom: 32 }}>
                    <FormLabel style={{ marginBottom: 8 }}>Warranty Document</FormLabel>
                    <ThemedButton
                      title={warrantyFile ? 'Replace Warranty' : 'Upload Warranty'}
                      onPress={() => setShowWarrantyModal(true)}
                      style={{ width: 220, alignSelf: 'flex-start' }}
                    />
                    {warrantyFile ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                        <Ionicons name="document" size={18} color={theme.accent} />
                        <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6 }}>Warranty</Text>
                        <TouchableOpacity onPress={() => setWarrantyFile(null)} style={{ marginLeft: 12 }}>
                          <Ionicons name="trash" size={22} color={theme.error || '#d32f2f'} />
                        </TouchableOpacity>
                      </View>
                    ) : null}
                  </View>

                  {/* Additional Documents */}
                  <View style={{ marginBottom: 32 }}>
                    <FormLabel style={{ marginBottom: 8 }}>Additional Documents (Max. {MAX_ADDITIONAL_DOCS})</FormLabel>
                    {additionalDocs.length > 0 ? (
                      <View style={{ gap: 8 }}>
                        {additionalDocs.map((d, idx) => (
                          <View key={`${d.uri}-${idx}`} style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Ionicons name="document-text" size={18} color={theme.accent} />
                            <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6, flex: 1 }} numberOfLines={1}>{d.name}</Text>
                            <TouchableOpacity onPress={() => removeAdditionalDocumentAt(idx)} style={{ marginLeft: 12 }}>
                              <Ionicons name="trash" size={20} color={theme.error || '#d32f2f'} />
                            </TouchableOpacity>
                          </View>
                        ))}
                      </View>
                    ) : (
                      <Text style={{ color: theme.secondaryText, fontSize: 14, marginBottom: 8 }}>No additional documents</Text>
                    )}
                    <ThemedButton
                      title={additionalDocs.length >= MAX_ADDITIONAL_DOCS ? 'Limit Reached' : 'Add Document'}
                      onPress={handlePickAdditionalDocument}
                      disabled={additionalDocs.length >= MAX_ADDITIONAL_DOCS}
                      style={{ width: 220, alignSelf: 'flex-start', marginTop: 8 }}
                    />
                  </View>

                  {/* Item Pictures */}
                  <FormLabel style={{ marginBottom: 8 }}>Item Pictures (Max. 3)</FormLabel>
                  {showImageLimitError && (
                    <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginBottom: 8 }}>
                      Maximum of 3 images per item, remove additional images
                    </Text>
                  )}
                  {Platform.OS === 'web' ? (
                    <WebImageGrid
                      pictures={toPictureArray(pictures)}
                      onReorder={handleDragEnd}
                      onDelete={(id) => handleDeletePicture(pictures.findIndex((p) => p.uri === id))}
                      onAdd={handlePickItemPictures}
                    />
                  ) : (
                    <NativeImageGrid
                      pictures={toPictureArray(pictures)}
                      onReorder={handleDragEnd}
                      onDelete={(id) => handleDeletePicture(pictures.findIndex((p) => p.uri === id))}
                    />
                  )}
                </View>
              </View>

              {/* Add Item Button */}
              <ThemedButton
                title={loading ? 'Adding...' : 'Add Item'}
                onPress={handleModernSave}
                disabled={loading}
                style={{ marginTop: 24 }}
                accessibilityLabel="Add Item"
              />
            </View>
          </View>
          {renderRoomModal()}
          {renderDatePicker()}
        </ScrollView>
      </KeyboardAvoidingView>
      {renderCategoryModal()}
      {renderSubcategoryModal()}
      {/* Add Additional Document - Name Modal (Web & Native) */}
      <Modal
        visible={showAddDocNameModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAddDocNameModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Name Document</Text>
            <RNTextInput
              style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
              placeholder="e.g. Art valuation"
              placeholderTextColor={theme.secondaryText}
              value={pendingAdditionalDocName}
              onChangeText={setPendingAdditionalDocName}
              maxLength={60}
            />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => { setShowAddDocNameModal(false); setPendingAdditionalDocFile(null); }} style={{ flex: 1 }} />
              <ThemedButton title="Add" onPress={confirmAddAdditionalDocument} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
      {/* Suggest Category Modal */}
      <Modal
        visible={showSuggestCategoryModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSuggestCategoryModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Suggest New Category</Text>
            <RNTextInput
              style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
              placeholder="Category name"
              placeholderTextColor={theme.secondaryText}
              value={proposedCategoryName}
              onChangeText={setProposedCategoryName}
            />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => setShowSuggestCategoryModal(false)} style={{ flex: 1 }} />
              <ThemedButton title={suggestLoading ? 'Submitting...' : 'Submit'} onPress={submitCategorySuggestion} disabled={suggestLoading} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
      {/* Suggest Subcategory Modal */}
      <Modal
        visible={showSuggestSubcategoryModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSuggestSubcategoryModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Suggest New Subcategory</Text>
            {selectedCategory ? (
              <Text style={{ color: theme.secondaryText, marginBottom: 6 }}>For: {selectedCategory.name}</Text>
            ) : null}
            <RNTextInput
              style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
              placeholder="Subcategory name"
              placeholderTextColor={theme.secondaryText}
              value={proposedSubcategoryName}
              onChangeText={setProposedSubcategoryName}
            />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => setShowSuggestSubcategoryModal(false)} style={{ flex: 1 }} />
              <ThemedButton title={suggestLoading ? 'Submitting...' : 'Submit'} onPress={submitSubcategorySuggestion} disabled={suggestLoading || !selectedCategory} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={showReceiptAdviceModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowReceiptAdviceModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
            <Text style={[styles.modalTitle, { color: theme.text, textAlign: 'center', marginBottom: 16 }]}>Add Purchase Details</Text>
            <Text style={{ color: theme.text, fontSize: 16, textAlign: 'center', marginBottom: 24 }}>{receiptAdviceMessage}</Text>
            <View style={{ width: '100%', gap: 12 }}>
              <ThemedButton
                title="Ignore and Continue"
                variant="secondary"
                onPress={() => {
                  setShowReceiptAdviceModal(false);
                  setShowReceiptModal(true);
                }}
                style={{ width: '100%' }}
              />
              <ThemedButton
                title="Go Back"
                variant="primary"
                onPress={() => setShowReceiptAdviceModal(false)}
                style={{ width: '100%' }}
              />
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={showReceiptModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowReceiptModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 16 }]}>Add Receipt</Text>
            <ThemedButton
              title="Image"
              onPress={async () => { setShowReceiptModal(false); await handlePickImage('receipt'); }}
              style={{ width: '100%', marginBottom: 16 }}
            />
            <ThemedButton
              title="PDF"
              onPress={async () => { setShowReceiptModal(false); await handlePickDocument('receipt'); }}
              style={{ width: '100%', marginBottom: 16 }}
            />
            <ThemedButton
              title="Select Existing"
              onPress={() => { setShowReceiptModal(false); setShowReceiptSelector(true); }}
              style={{ width: '100%', marginBottom: 16 }}
            />
            <ThemedButton
              title="Cancel"
              variant="secondary"
              onPress={() => setShowReceiptModal(false)}
              style={{ width: '100%' }}
            />
          </View>
        </View>
      </Modal>
      <Modal
        visible={showWarrantyModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowWarrantyModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 16 }]}>Add Warranty</Text>
            <ThemedButton
              title="Image"
              onPress={async () => { setShowWarrantyModal(false); await handlePickImage('warranty'); }}
              style={{ width: '100%', marginBottom: 16 }}
            />
            <ThemedButton
              title="PDF"
              onPress={async () => { setShowWarrantyModal(false); await handlePickDocument('warranty'); }}
              style={{ width: '100%', marginBottom: 16 }}
            />
            <ThemedButton
              title="Cancel"
              variant="secondary"
              onPress={() => setShowWarrantyModal(false)}
              style={{ width: '100%' }}
            />
          </View>
        </View>
      </Modal>
      <ReceiptSelector
        visible={showReceiptSelector}
        filterPlace={placeOfPurchase || null}
        onClose={() => setShowReceiptSelector(false)}
        onSelect={(r) => {
          setSelectedReceipt(r);
          setReceiptFile(null);
          setShowReceiptSelector(false);
        }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    marginBottom: 30,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
  },
  backButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  form: {
    width: '100%',
  },
  webContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  formColumn: {
    width: '100%',
    marginBottom: 16,
  },
  webForm: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
    alignItems: 'flex-start',
  },
  webFormColumn: {
    flex: 1,
    minWidth: 300,
    maxWidth: 400,
  },
  label: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 8,
    marginTop: 0,
  },
  input: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    color: '#fff',
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    marginBottom: 16,
    height: 44,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  dateButton: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    height: 44,
  },
  dateButtonText: {
    color: '#fff',
    fontSize: 16,
  },
  fileButtons: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  fileButton: {
    flex: 1,
    minWidth: 150,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
  },
  fileButtonText: {
    color: '#007AFF',
    fontSize: 16,
  },
  fileName: {
    color: '#666',
    fontSize: 14,
    marginTop: 4,
  },
  roomSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    height: 44,
  },
  roomSelectorText: {
    color: '#fff',
    fontSize: 16,
  },
  selectedRoom: {
    backgroundColor: 'rgba(0,122,255,0.1)',
  },
  selectedRoomText: {},
  datePickerContainer: {
    padding: 20,
    gap: 16,
  },
  confirmButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  picturesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  pictureWrapper: {
    position: 'relative',
  },
  picture: {
    width: 100,
    height: 100,
    borderRadius: 8,
  },
  deletePictureButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#fff',
    borderRadius: 12,
  },
  addPictureButton: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
  },
  addPictureText: {
    color: '#007AFF',
    fontSize: 12,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#2a2a2a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    color: '#fff',
  },
  closeButton: {
    padding: 8,
  },
  roomOption: {
    padding: 16,
  },
  roomOptionText: {
    fontSize: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteModalContent: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  deleteModalTitle: {
    fontSize: 17,
    fontWeight: '500',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  deleteModalText: {
    fontSize: 16,
    color: '#fff',
    marginBottom: 20,
    textAlign: 'center',
  },
  deleteModalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
  },
  deleteModalButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  datePickerModalContent: {
    maxWidth: 400,
    width: '90%',
    borderRadius: 12,
    padding: 24,
  },
  datePickerButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    maxWidth: 300,
  },
  datePickerButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelDateButton: {
    backgroundColor: '#333',
  },
  confirmDateButton: {
    backgroundColor: '#007AFF',
  },
  datePickerButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  documentSection: {
    flexDirection: 'row',
    gap: 24,
  },
  documentColumn: {
    flex: 1,
  },
  picturesColumn: {
    flex: 1,
  },
  serialNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  serialNumberInput: {
    flex: 1,
    marginBottom: 0,
    height: 44,
  },
  scanButton: {
    height: 44,
    width: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
  },
  scannerContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  scannerOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scannerText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: 16,
    borderRadius: 8,
  },
  inputGroup: {
    marginBottom: 15,
  },
}); 